import React from "react";

import isEmpty from "lodash/isEmpty";

import { useLegalAuditDocumentRedaction } from "manager/hooks/useLegalAuditDocumentRedaction";
import FlexContainer from "shared/components/FlexContainer";
import Icon from "shared/components/Icon";
import { UnderlineLink } from "shared/components/Links";
import Tile from "shared/components/Tile";
import { CollapseButton } from "shared/components/Tile/styled";
import {
  MediumTitle,
  RegularText,
  SmallText,
} from "shared/components/Typography";
import type { OcrResult } from "shared/interfaces/api/document";
import { getFraudRiskTextAndColor } from "shared/utils/document";
import { printDollars } from "shared/utils/dollar-print";
import { formatDate } from "shared/utils/ui";

import {
  Divider,
  IncomeStreamDetailTable,
  IncomeStreamsTable,
  IncomeStreamHeader,
} from "../IncomeStreamsSection/styled";

interface OcrCashBalanceAccount {
  accountType?: string;
  accountNumber?: string;
  accountHolder?: string;
  endingBalance?: number;
  statementPeriod?: {
    startDate?: string;
    endDate?: string;
  };
  sourceDocumentId?: string;
  sourceProviderGroup?: string;
  sourceOcrResultId?: string;
}

interface UnverifiedPotentialBalancesProps {
  accounts: OcrCashBalanceAccount[];
  ocrResults?: OcrResult[];
  canViewOcrResults?: boolean;
  subtitleText?: string;
  excludedReasons?: string[];
  bankStatementSourceLabel?: string;
  documentSourceLabel?: string;
}

interface UnverifiedBalanceRow {
  key: string;
  source: string;
  excludedReasons: string[];
  name: string;
  cashAvailable: string;
  sourceOcrResultId?: string;
  owner: string;
  accountType: string;
  statementPeriod: string;
  sourceDocumentName: string;
  sourceDocumentUrl?: string;
}

export const UnverifiedPotentialBalances = ({
  accounts,
  ocrResults = [],
  canViewOcrResults = false,
  subtitleText = "This discovered cash balance is not included in the total cash balance.",
  excludedReasons = [
    "Extracted from documents",
    "Manual review required before inclusion",
  ],
  bankStatementSourceLabel = "Bank Statement Upload",
  documentSourceLabel = "Document Upload",
}: UnverifiedPotentialBalancesProps) => {
  const { shouldRedactDocuments } = useLegalAuditDocumentRedaction();
  const getMatchedOcrResult = (sourceOcrResultId?: string) =>
    ocrResults.find(
      (ocrResult: any) =>
        ocrResult?.id === sourceOcrResultId ||
        ocrResult?.ocrResultId === sourceOcrResultId
    );

  const shouldApplyBankStatementGuard = !isEmpty(ocrResults);
  const accountsForRender = shouldApplyBankStatementGuard
    ? accounts.filter((account) => {
        const matchedOcrResult: any = getMatchedOcrResult(
          account.sourceOcrResultId
        );
        return matchedOcrResult?.formType === "bank-statement";
      })
    : accounts;
  const hasData = !isEmpty(accountsForRender);

  const buildStatementPeriod = (account: OcrCashBalanceAccount) => {
    const startDate = account.statementPeriod?.startDate;
    const endDate = account.statementPeriod?.endDate;
    const formattedStartDate = startDate ? formatDate(startDate) : null;
    const formattedEndDate = endDate ? formatDate(endDate) : null;

    if (formattedStartDate && formattedEndDate) {
      return `${formattedStartDate} to ${formattedEndDate}`;
    }
    if (formattedEndDate) return `Ending ${formattedEndDate}`;
    if (formattedStartDate) return `Starting ${formattedStartDate}`;

    return "N/A";
  };

  const determineFraudRisk = (sourceOcrResultId?: string) => {
    if (!canViewOcrResults || !sourceOcrResultId) {
      return { text: "N/A", color: "unset" };
    }

    const matchedOcrResult: any = getMatchedOcrResult(sourceOcrResultId);
    const fraudRiskLevel = matchedOcrResult?.fraudRiskLevel;

    return fraudRiskLevel
      ? getFraudRiskTextAndColor(fraudRiskLevel)
      : { text: "N/A", color: "unset" };
  };

  const resolveSourceDocumentName = (account: OcrCashBalanceAccount) => {
    const matchedOcrResult: any = getMatchedOcrResult(
      account.sourceOcrResultId
    );

    if (matchedOcrResult?.documentName) return matchedOcrResult.documentName;
    if (matchedOcrResult?.storageName) return matchedOcrResult.storageName;

    return "N/A";
  };

  const resolveSourceDocumentUrl = (account: OcrCashBalanceAccount) => {
    const matchedOcrResult: any = getMatchedOcrResult(
      account.sourceOcrResultId
    );
    return matchedOcrResult?.url;
  };

  const buildAccountName = (accountNumber?: string) => {
    if (!accountNumber) return "OCR account";

    const normalized = accountNumber.replace(/\s+/g, "");
    const mask = normalized.slice(-4);

    return mask ? `Account (*${mask})` : "Account";
  };

  const buildSourceLabel = (sourceProviderGroup?: string) => {
    if (!sourceProviderGroup) return documentSourceLabel;
    return sourceProviderGroup.startsWith("vega/")
      ? bankStatementSourceLabel
      : documentSourceLabel;
  };

  const rows: UnverifiedBalanceRow[] = accountsForRender.map(
    (account, index) => ({
      key: `unverified-potential-balance-${index}`,
      source: buildSourceLabel(account.sourceProviderGroup),
      excludedReasons,
      name: buildAccountName(account.accountNumber),
      cashAvailable: printDollars(account.endingBalance ?? 0),
      sourceOcrResultId: account.sourceOcrResultId,
      owner: account.accountHolder || "N/A",
      accountType: account.accountType || "N/A",
      statementPeriod: buildStatementPeriod(account),
      sourceDocumentName: resolveSourceDocumentName(account),
      sourceDocumentUrl: resolveSourceDocumentUrl(account),
    })
  );

  return (
    <>
      <Divider />
      <Tile
        id="unverified-potential-balances"
        header={{
          collapsibleOnDesktop: true,
          collapsibleOnMobile: true,
          title: <MediumTitle>Potential Balances</MediumTitle>,
          subtitle: hasData && (
            <FlexContainer flexDirection="column" gap="4px">
              <SmallText>{subtitleText}</SmallText>
            </FlexContainer>
          ),
          withoutBorderBottom: true,
        }}
        rounded
      >
        {!hasData ? (
          <Tile.Inner
            noPaddingTop
            data-testid="unverified-potential-balances-empty"
          >
            <RegularText>
              No potential balances available.
            </RegularText>
          </Tile.Inner>
        ) : (
          <Tile.Inner noPaddingTop>
            <IncomeStreamsTable
              columns={[
                {
                  title: <IncomeStreamHeader>Source</IncomeStreamHeader>,
                  dataIndex: "source",
                  key: "source",
                  width: 150,
                },
                {
                  title: (
                    <IncomeStreamHeader>Exclusion Reason(s)</IncomeStreamHeader>
                  ),
                  dataIndex: "excludedReasons",
                  key: "excludedReasons",
                  width: 260,
                  render: (reasons: string[]) => (
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {reasons.map((reason) => (
                        <li key={reason}>
                          <RegularText>{reason}</RegularText>
                        </li>
                      ))}
                    </ul>
                  ),
                },
                {
                  title: <IncomeStreamHeader>Fraud Risk</IncomeStreamHeader>,
                  dataIndex: "fraudRisk",
                  key: "fraudRisk",
                  width: 140,
                  render: (_: unknown, record: UnverifiedBalanceRow) => {
                    const fraudRisk = determineFraudRisk(
                      record.sourceOcrResultId
                    );
                    return (
                      <RegularText style={{ color: fraudRisk.color }}>
                        {fraudRisk.text}
                      </RegularText>
                    );
                  },
                },
                {
                  title: <IncomeStreamHeader>Account Name</IncomeStreamHeader>,
                  dataIndex: "name",
                  key: "name",
                  width: 220,
                },
                {
                  title: (
                    <IncomeStreamHeader>Cash Available</IncomeStreamHeader>
                  ),
                  dataIndex: "cashAvailable",
                  key: "cashAvailable",
                  width: 150,
                },
                {
                  title: "",
                  key: "expand",
                  width: 50,
                },
              ]}
              dataSource={rows}
              expandIcon={({ record, expanded, onExpand }: any) => (
                <CollapseButton
                  collapsed={!expanded}
                  collapsibleOnDesktop
                  collapsibleOnMobile
                  onClick={(event: any) => onExpand(record, event)}
                  style={{ float: "right" }}
                >
                  <Icon.ChevronDownIcon
                    style={{ display: "block", margin: "auto" }}
                  />
                </CollapseButton>
              )}
              expandIconAsCell={false}
              expandIconColumnIndex={5}
              expandedRowRender={(record: UnverifiedBalanceRow) => (
                <IncomeStreamDetailTable
                  columns={[
                    {
                      title: (
                        <IncomeStreamHeader>Account Owner</IncomeStreamHeader>
                      ),
                      dataIndex: "owner",
                      key: "owner",
                      width: 180,
                    },
                    {
                      title: (
                        <IncomeStreamHeader>Account Type</IncomeStreamHeader>
                      ),
                      dataIndex: "accountType",
                      key: "accountType",
                      width: 160,
                    },
                    {
                      title: (
                        <IncomeStreamHeader>
                          Statement Period
                        </IncomeStreamHeader>
                      ),
                      dataIndex: "statementPeriod",
                      key: "statementPeriod",
                      width: 220,
                    },
                    {
                      title: (
                        <IncomeStreamHeader>Source Document</IncomeStreamHeader>
                      ),
                      dataIndex: "sourceDocumentName",
                      key: "sourceDocumentName",
                      width: 240,
                      render: (_: unknown, record: UnverifiedBalanceRow) =>
                        record.sourceDocumentUrl && !shouldRedactDocuments ? (
                          <UnderlineLink
                            href={record.sourceDocumentUrl}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {record.sourceDocumentName}
                          </UnderlineLink>
                        ) : (
                          <RegularText>{record.sourceDocumentName}</RegularText>
                        ),
                    },
                  ]}
                  dataSource={[record]}
                  noBorderBottom
                  noClickableRows
                  pagination={false}
                  rowKey="key"
                  size="small"
                />
              )}
              expandRowByClick
              noBorderBottom
              noClickableRows
              pagination={false}
              scroll={{ x: 980 }}
              size="middle"
            />
          </Tile.Inner>
        )}
      </Tile>
    </>
  );
};
